import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaArrowLeft,
  FaAward,
  FaCamera,
  FaCheck,
  FaDownload,
  FaExclamationCircle,
  FaFileAlt,
  FaFlask,
  FaHome,
  FaInfoCircle,
  FaSave,
  FaTimes,
  FaUpload,
} from "react-icons/fa";
import CommonService from "../../services/common.service";
import StorageService from "../../services/StorageService";
import { API_URLS } from "../../constants/api-urls";
import "../../assets/styles/seller_cm_internal_inspection_report.scss";

function extractApiErrorMessage(err) {
  const d = err?.response?.data;
  return d?.message || d?.error || err?.message || "Request failed";
}

function unwrapQcEnvelope(res) {
  return res?.data?.data ?? res?.data ?? res;
}

const CAT_LABEL = {
  visual: "Visual Inspection",
  dimension: "Dimensional Check",
  functional: "Functional Test",
  packaging: "Packaging / Labeling",
  documentation: "Documentation",
};

const DEFAULT_CHECKLIST = [
  { id: "d1", category: "Visual Inspection", title: "Surface finish quality", criteria: "Acceptance per agreed visual standard" },
  { id: "d2", category: "Visual Inspection", title: "Component alignment", criteria: "Components aligned within tolerance" },
  { id: "d3", category: "Visual Inspection", title: "Color consistency", criteria: "Uniform color vs reference sample" },
  { id: "d4", category: "Dimensional Check", title: "Length tolerance (+/- 0.5mm)", criteria: "Measured length within spec" },
  { id: "d5", category: "Dimensional Check", title: "Width tolerance (+/- 0.5mm)", criteria: "Measured width within spec" },
  { id: "d6", category: "Functional Test", title: "Performance test", criteria: "Functional criteria per specification" },
  { id: "d7", category: "Safety Check", title: "Electrical safety", criteria: "Applicable safety requirements" },
  { id: "d8", category: "Safety Check", title: "Material safety", criteria: "Material compliance requirements" },
];

const COMPLIANCE_STANDARDS = [
  { id: "iso9001", label: "ISO 9001:2015", sub: "Quality Management" },
  { id: "rohs", label: "RoHS Compliance", sub: "Hazardous Substances" },
  { id: "ce", label: "CE Marking", sub: "European Conformity" },
  { id: "fcc", label: "FCC Certification", sub: "Electromagnetic Interference" },
];

const AQL_OPTIONS = [
  { value: "1.0", label: "1.0 (Tight)" },
  { value: "1.5", label: "1.5" },
  { value: "2.5", label: "2.5 (Standard)" },
  { value: "4.0", label: "4.0 (Relaxed)" },
];

function buildChecklistFromQcPayload(payload) {
  const raw = payload?.inspectionChecklistItems;
  if (!Array.isArray(raw) || raw.length === 0) {
    return DEFAULT_CHECKLIST.map((row) => ({ ...row, status: "pending" }));
  }
  return raw.map((it, idx) => ({
    id: String(it?.id ?? `c-${idx}`),
    category: CAT_LABEL[it?.category] || String(it?.category || "Inspection"),
    title: String(it?.description || `Check ${idx + 1}`).trim() || `Check ${idx + 1}`,
    criteria: String(it?.acceptanceCriteria || "—").trim() || "—",
    status: "pending",
  }));
}

function mergeDraftChecklist(baseRows, draftRows) {
  if (!Array.isArray(draftRows) || draftRows.length === 0) return baseRows;
  const byId = new Map(draftRows.map((r) => [r.id, r]));
  return baseRows.map((row) => {
    const d = byId.get(row.id);
    if (d && (d.status === "pass" || d.status === "fail" || d.status === "pending")) {
      return { ...row, status: d.status };
    }
    return row;
  });
}

/** Internal QC docs from agreed QC payload (`internalQcDocs`) — same source as QC modal "Internal QC Docs" tab. */
function normalizeInternalQcDocs(payload) {
  const raw = payload?.internalQcDocs;
  if (!Array.isArray(raw) || raw.length === 0) return [];
  return [...raw]
    .map((d, idx) => ({
      id: String(d?.id ?? `iqc-${idx}`),
      name: String(d?.name || "").trim() || `Internal QC document ${idx + 1}`,
      fileFormat: String(d?.fileFormat || "pdf").toLowerCase().replace(/^\./, ""),
      required: d?.required !== false,
      order: typeof d?.order === "number" ? d.order : idx + 1,
    }))
    .sort((a, b) => a.order - b.order);
}

function acceptForDocFormat(fileFormat) {
  const f = String(fileFormat || "pdf").toLowerCase();
  if (f === "pdf") return ".pdf,application/pdf";
  if (f === "jpg" || f === "jpeg") return "image/jpeg";
  if (f === "png") return "image/png";
  if (f === "csv") return ".csv,text/csv";
  if (f === "xlsx" || f === "xls") return ".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  if (f === "doc" || f === "docx") return ".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  return "*/*";
}

function formatHintForDoc(doc) {
  const fmt = String(doc.fileFormat || "pdf").toUpperCase();
  return `Accepted: ${fmt} (max 10MB)`;
}

const CERT_ICONS = [FaFileAlt, FaAward, FaFlask, FaUpload];

/** QC norms fields from agreed payload (same keys as BuyerCmQcRequirementsModal norms tab). */
function readQcNormsSampleFields(payload) {
  if (!payload || typeof payload !== "object") {
    return { aql: "2.5", samplePct: "", minPassPct: "" };
  }
  const aql = typeof payload.aql === "string" && payload.aql.trim() ? payload.aql.trim() : "2.5";
  const samplePct =
    payload.samplePct != null && String(payload.samplePct).trim() !== "" ? String(payload.samplePct).trim() : "";
  const minPassPct =
    payload.minPassPct != null && String(payload.minPassPct).trim() !== "" ? String(payload.minPassPct).trim() : "";
  return { aql, samplePct, minPassPct };
}

function suggestedSamplesFromBatch(batchStr, samplePctNorm) {
  const nBatch = Number(String(batchStr ?? "").replace(/,/g, "").trim());
  const nPct = Number(String(samplePctNorm ?? "").trim());
  if (!Number.isFinite(nBatch) || nBatch <= 0 || !Number.isFinite(nPct) || nPct <= 0) return null;
  return String(Math.max(1, Math.ceil((nBatch * nPct) / 100)));
}

const EMPTY_UPLOAD_DISPLAY = { comprehensive: "", photos: [], measurement: "", nonConformance: "" };

function isValidPositiveNumberStr(s) {
  const n = Number(String(s ?? "").replace(/,/g, "").trim());
  return Number.isFinite(n) && n > 0;
}

function parseUploadReportsSnapshot(ur) {
  if (!ur || typeof ur !== "object") {
    return { ...EMPTY_UPLOAD_DISPLAY };
  }
  return {
    comprehensive: typeof ur.comprehensive?.fileName === "string" ? ur.comprehensive.fileName.trim() : "",
    photos: Array.isArray(ur.photos?.fileNames)
      ? ur.photos.fileNames.filter((x) => typeof x === "string" && x.trim())
      : [],
    measurement: typeof ur.measurement?.fileName === "string" ? ur.measurement.fileName.trim() : "",
    nonConformance: typeof ur.nonConformance?.fileName === "string" ? ur.nonConformance.fileName.trim() : "",
  };
}

export default function SellerCmInternalInspectionReportPage() {
  const { rfqId: rfqIdParam } = useParams();
  const navigate = useNavigate();
  const rfqId = Number(rfqIdParam);

  const [loading, setLoading] = useState(true);
  const [qcPayload, setQcPayload] = useState(null);
  const [rfqMeta, setRfqMeta] = useState(null);
  const [readOnly, setReadOnly] = useState(false);
  const [saving, setSaving] = useState(false);
  /** After a failed submit, highlights empty mandatory fields */
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const [activeTab, setActiveTab] = useState("results");
  const [facilityName, setFacilityName] = useState("");
  const [inspectorName, setInspectorName] = useState("");
  const [inspectionDate, setInspectionDate] = useState(() => new Date().toISOString().slice(0, 10));

  const [checklist, setChecklist] = useState(() => DEFAULT_CHECKLIST.map((r) => ({ ...r, status: "pending" })));

  const [batchSize, setBatchSize] = useState("");
  const [samplesInspected, setSamplesInspected] = useState("");
  const [aqlApplied, setAqlApplied] = useState("2.5");
  const [labTestResults, setLabTestResults] = useState("");
  const [complianceStatus, setComplianceStatus] = useState(() =>
    COMPLIANCE_STANDARDS.reduce((acc, s) => {
      acc[s.id] = "pending";
      return acc;
    }, {})
  );

  /** Selected files keyed by internal QC doc id */
  const [certFiles, setCertFiles] = useState({});
  /** Persisted file names from draft (survives refetch) */
  const [certDisplayNames, setCertDisplayNames] = useState({});
  const [uploadFiles, setUploadFiles] = useState({
    comprehensive: null,
    photos: [],
    measurement: null,
    nonConformance: null,
  });
  /** File names for Upload Reports tab (restored from draft / survives refetch) */
  const [uploadDisplayNames, setUploadDisplayNames] = useState(() => ({ ...EMPTY_UPLOAD_DISPLAY }));

  const loadData = useCallback(async () => {
    if (!Number.isFinite(rfqId) || rfqId <= 0) {
      toast.error("Invalid RFQ");
      navigate("/seller/cm-rfqs");
      return;
    }
    setLoading(true);
    try {
      const [qcRes, listRes] = await Promise.all([
        CommonService.get(API_URLS.CONTRACT_MANUFACTURER.SELLER_QC_REQUIREMENTS_GET(rfqId)),
        CommonService.get(API_URLS.CONTRACT_MANUFACTURER.SELLER_RFQS_RECEIVED),
      ]);
      const qcData = unwrapQcEnvelope(qcRes);
      const payload = qcData?.qc?.qc_payload_json ?? qcData?.qc?.qcPayloadJson ?? null;
      if (!payload || typeof payload !== "object") {
        toast.error("QC requirements not found");
        navigate("/seller/cm-rfqs");
        return;
      }
      if (!payload.buyerQcNormsApprovedAt) {
        toast.error("QC norms must be approved first");
        navigate("/seller/cm-rfqs");
        return;
      }
      if (!payload.cmInternalInspectionStartedAt) {
        toast.error("Start inspection from QC requirements first");
        navigate("/seller/cm-rfqs");
        return;
      }
      const submitted = !!payload.cmInternalInspectionReportSubmittedAt;
      setReadOnly(submitted);
      setQcPayload(payload);

      const listPayload = listRes?.data ?? {};
      const items = Array.isArray(listPayload?.items) ? listPayload.items : [];
      const row = items.find((it) => Number(it.id) === rfqId);
      setRfqMeta(row || null);

      const base = buildChecklistFromQcPayload(payload);
      const norms = readQcNormsSampleFields(payload);
      const draftRaw = payload.cmInternalInspectionReportDraft;
      const reportDataRaw = payload.cmInternalInspectionReportData;
      const draftObj = draftRaw && typeof draftRaw === "object" ? draftRaw : null;
      const reportObj = reportDataRaw && typeof reportDataRaw === "object" ? reportDataRaw : null;
      const snapshot =
        draftObj && Object.keys(draftObj).length > 0 ? draftObj : reportObj && Object.keys(reportObj).length > 0 ? reportObj : null;

      setCertFiles({});
      setUploadFiles({ comprehensive: null, photos: [], measurement: null, nonConformance: null });

      if (snapshot) {
        if (typeof snapshot.facilityName === "string") setFacilityName(snapshot.facilityName);
        if (typeof snapshot.inspectorName === "string") setInspectorName(snapshot.inspectorName);
        if (typeof snapshot.inspectionDate === "string") setInspectionDate(snapshot.inspectionDate.slice(0, 10));
        if (typeof snapshot.batchSize === "string") setBatchSize(snapshot.batchSize);
        if (typeof snapshot.samplesInspected === "string" && String(snapshot.samplesInspected).trim() !== "") {
          setSamplesInspected(snapshot.samplesInspected);
        } else if (typeof snapshot.batchSize === "string" && String(snapshot.batchSize).trim() && norms.samplePct) {
          const sug = suggestedSamplesFromBatch(snapshot.batchSize, norms.samplePct);
          if (sug) setSamplesInspected(sug);
        } else {
          setSamplesInspected("");
        }
        if (typeof snapshot.aqlApplied === "string" && String(snapshot.aqlApplied).trim() !== "") {
          setAqlApplied(snapshot.aqlApplied.trim());
        } else {
          setAqlApplied(norms.aql);
        }
        if (typeof snapshot.labTestResults === "string") setLabTestResults(snapshot.labTestResults);
        if (snapshot.complianceStatus && typeof snapshot.complianceStatus === "object") {
          setComplianceStatus((prev) => ({ ...prev, ...snapshot.complianceStatus }));
        }
        if (Array.isArray(snapshot.checklist)) {
          setChecklist(mergeDraftChecklist(base, snapshot.checklist));
        } else {
          setChecklist(base);
        }
        if (typeof snapshot.activeTab === "string") setActiveTab(snapshot.activeTab);
        if (snapshot.certificateUploads && typeof snapshot.certificateUploads === "object") {
          const names = {};
          Object.entries(snapshot.certificateUploads).forEach(([docId, v]) => {
            if (v && typeof v === "object" && typeof v.fileName === "string" && v.fileName.trim()) {
              names[docId] = v.fileName.trim();
            }
          });
          setCertDisplayNames(names);
        } else {
          setCertDisplayNames({});
        }
        setUploadDisplayNames(parseUploadReportsSnapshot(snapshot.uploadReports));
      } else {
        setChecklist(base);
        setCertDisplayNames({});
        setUploadDisplayNames({ ...EMPTY_UPLOAD_DISPLAY });
        setFacilityName("");
        setInspectorName("");
        setInspectionDate(new Date().toISOString().slice(0, 10));
        setAqlApplied(norms.aql);
        setBatchSize("");
        setSamplesInspected("");
        setLabTestResults("");
        setComplianceStatus(
          COMPLIANCE_STANDARDS.reduce((acc, s) => {
            acc[s.id] = "pending";
            return acc;
          }, {})
        );
        setActiveTab("results");
      }
    } catch (e) {
      toast.error(extractApiErrorMessage(e));
      navigate("/seller/cm-rfqs");
    } finally {
      setLoading(false);
    }
  }, [navigate, rfqId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const stats = useMemo(() => {
    const total = checklist.length;
    let passed = 0;
    let failed = 0;
    let pending = 0;
    checklist.forEach((c) => {
      if (c.status === "pass") passed += 1;
      else if (c.status === "fail") failed += 1;
      else pending += 1;
    });
    const done = passed + failed;
    const pct = total === 0 ? 0 : Math.round((done / total) * 100);
    return { total, passed, failed, pending, pct };
  }, [checklist]);

  const certificateSlots = useMemo(() => normalizeInternalQcDocs(qcPayload), [qcPayload]);

  const qcNormsFromPayload = useMemo(() => readQcNormsSampleFields(qcPayload), [qcPayload]);

  const aqlSelectOptions = useMemo(() => {
    const normAql = qcNormsFromPayload.aql;
    const has = AQL_OPTIONS.some((o) => o.value === normAql);
    if (normAql && !has) {
      return [...AQL_OPTIONS, { value: normAql, label: `${normAql} (QC norms)` }];
    }
    return AQL_OPTIONS;
  }, [qcNormsFromPayload.aql]);

  useEffect(() => {
    if (readOnly) return;
    if (!qcNormsFromPayload.samplePct) return;
    const suggested = suggestedSamplesFromBatch(batchSize, qcNormsFromPayload.samplePct);
    if (!suggested) return;
    setSamplesInspected((prev) => (String(prev ?? "").trim() === "" ? suggested : prev));
  }, [batchSize, qcNormsFromPayload.samplePct, readOnly]);

  function setCheckStatus(id, status) {
    if (readOnly) return;
    setChecklist((rows) => rows.map((r) => (r.id === id ? { ...r, status } : r)));
  }

  function buildCertificateUploadsPayload() {
    const out = {};
    certificateSlots.forEach((doc) => {
      const file = certFiles[doc.id];
      const name = file?.name || certDisplayNames[doc.id] || null;
      out[doc.id] = { fileName: name };
    });
    return out;
  }

  function buildUploadReportsPayload() {
    const photoNames =
      uploadFiles.photos?.length > 0
        ? uploadFiles.photos.map((f) => f.name)
        : Array.isArray(uploadDisplayNames.photos)
          ? uploadDisplayNames.photos.filter((n) => typeof n === "string" && n.trim())
          : [];
    return {
      comprehensive: {
        fileName:
          uploadFiles.comprehensive?.name ||
          (typeof uploadDisplayNames.comprehensive === "string" && uploadDisplayNames.comprehensive.trim()
            ? uploadDisplayNames.comprehensive.trim()
            : null),
      },
      photos: { fileNames: photoNames },
      measurement: {
        fileName:
          uploadFiles.measurement?.name ||
          (typeof uploadDisplayNames.measurement === "string" && uploadDisplayNames.measurement.trim()
            ? uploadDisplayNames.measurement.trim()
            : null),
      },
      nonConformance: {
        fileName:
          uploadFiles.nonConformance?.name ||
          (typeof uploadDisplayNames.nonConformance === "string" && uploadDisplayNames.nonConformance.trim()
            ? uploadDisplayNames.nonConformance.trim()
            : null),
      },
    };
  }

  function buildDraftPayload() {
    return {
      facilityName,
      inspectorName,
      inspectionDate,
      batchSize,
      samplesInspected,
      aqlApplied,
      labTestResults,
      complianceStatus,
      checklist: checklist.map((c) => ({ id: c.id, status: c.status })),
      certificateUploads: buildCertificateUploadsPayload(),
      uploadReports: buildUploadReportsPayload(),
      activeTab,
      savedAt: new Date().toISOString(),
    };
  }

  function validateRequiredCertificates() {
    for (const doc of certificateSlots) {
      if (!doc.required) continue;
      const file = certFiles[doc.id];
      const persisted = certDisplayNames[doc.id];
      if (!file && !persisted) {
        return `Required document not uploaded: ${doc.name}`;
      }
    }
    return null;
  }

  /** Required uploads per UI (banner on Upload Reports tab). */
  function validateUploadReportsForSubmit() {
    const comp =
      uploadFiles.comprehensive?.name ||
      (typeof uploadDisplayNames.comprehensive === "string" && uploadDisplayNames.comprehensive.trim());
    const photoCount =
      (uploadFiles.photos?.length || 0) ||
      (Array.isArray(uploadDisplayNames.photos) ? uploadDisplayNames.photos.filter(Boolean).length : 0);
    if (!comp) {
      return { message: "Comprehensive inspection report is required.", tab: "uploads" };
    }
    if (!photoCount) {
      return { message: "At least one inspection photo is required.", tab: "uploads" };
    }
    return null;
  }

  /**
   * Full mandatory validation for submit (not for save draft).
   * Returns first blocking issue with tab to show.
   */
  function validateAllMandatoryFieldsForSubmit() {
    if (!String(facilityName || "").trim()) {
      return { message: "Facility / team name is required.", tab: "results" };
    }
    if (!String(inspectorName || "").trim()) {
      return { message: "Inspector name is required.", tab: "results" };
    }
    if (!String(inspectionDate || "").trim()) {
      return { message: "Inspection date is required.", tab: "results" };
    }

    const bs = String(batchSize || "").replace(/,/g, "").trim();
    if (!bs) {
      return { message: "Batch size is required.", tab: "compliance" };
    }
    const nBatch = Number(bs);
    if (!Number.isFinite(nBatch) || nBatch <= 0) {
      return { message: "Batch size must be a positive number.", tab: "compliance" };
    }

    const ss = String(samplesInspected || "").replace(/,/g, "").trim();
    if (!ss) {
      return { message: "Samples inspected is required.", tab: "compliance" };
    }
    const nSamples = Number(ss);
    if (!Number.isFinite(nSamples) || nSamples <= 0) {
      return { message: "Samples inspected must be a positive number.", tab: "compliance" };
    }
    if (nSamples > nBatch) {
      return { message: "Samples inspected cannot be greater than batch size.", tab: "compliance" };
    }

    if (!String(aqlApplied || "").trim()) {
      return { message: "AQL applied is required.", tab: "compliance" };
    }

    const pendingCheck = checklist.find((c) => c.status === "pending");
    if (pendingCheck) {
      return {
        message: `Each inspection check must be Pass or Fail. Still pending: "${pendingCheck.title}".`,
        tab: "results",
      };
    }

    for (const s of COMPLIANCE_STANDARDS) {
      const st = complianceStatus[s.id] || "pending";
      if (st === "pending") {
        return {
          message: `Each compliance standard must be Pass or Fail. Still pending: ${s.label}.`,
          tab: "compliance",
        };
      }
    }

    if (!String(labTestResults || "").trim()) {
      return {
        message: "Lab test results are required (enter findings or type N/A if not applicable).",
        tab: "compliance",
      };
    }

    const certMsg = validateRequiredCertificates();
    if (certMsg) {
      return { message: certMsg, tab: "certificates" };
    }

    const uploadErr = validateUploadReportsForSubmit();
    if (uploadErr) return uploadErr;

    const hasFailedCheck = checklist.some((c) => c.status === "fail");
    if (hasFailedCheck) {
      const nc =
        uploadFiles.nonConformance?.name ||
        (typeof uploadDisplayNames.nonConformance === "string" && uploadDisplayNames.nonConformance.trim());
      if (!nc) {
        return {
          message: "Non-conformance report is required when at least one inspection check is marked Fail.",
          tab: "uploads",
        };
      }
    }

    return null;
  }

  async function handleSaveDraft() {
    if (readOnly) return;
    setSaving(true);
    try {
      const body = {
        mode: "inspectionUpdate",
        payload: { cmInternalInspectionReportDraft: buildDraftPayload() },
      };
      const res = await CommonService.post(API_URLS.CONTRACT_MANUFACTURER.SELLER_QC_REQUIREMENTS_APPLY(rfqId), body);
      if (res && typeof res === "object" && res.success === false) {
        throw new Error(res.message || "Save failed");
      }
      toast.success("Draft saved");
      setSubmitAttempted(false);
      await loadData();
    } catch (e) {
      toast.error(extractApiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  async function handleSubmitReport() {
    if (readOnly) return;
    setSubmitAttempted(true);
    const validationErr = validateAllMandatoryFieldsForSubmit();
    if (validationErr) {
      toast.error(validationErr.message);
      setActiveTab(validationErr.tab);
      return;
    }
    setSaving(true);
    try {
      const draft = buildDraftPayload();
      const body = {
        mode: "inspectionUpdate",
        payload: {
          cmInternalInspectionReportSubmittedAt: new Date().toISOString(),
          cmInternalInspectionReportData: draft,
          cmInternalInspectionReportDraft: draft,
        },
      };
      const res = await CommonService.post(API_URLS.CONTRACT_MANUFACTURER.SELLER_QC_REQUIREMENTS_APPLY(rfqId), body);
      if (res && typeof res === "object" && res.success === false) {
        throw new Error(res.message || "Submit failed");
      }
      toast.success("Inspection report submitted");
      setSubmitAttempted(false);
      navigate("/seller/cm-rfqs");
    } catch (e) {
      toast.error(extractApiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  function handleExport() {
    const blob = new Blob([JSON.stringify(buildDraftPayload(), null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `internal-qc-inspection-rfq-${rfqId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const productCode = String(qcPayload?.productNameCode || rfqMeta?.projectTitle || `RFQ-${rfqId}`).trim();
  const buyerCo = String(rfqMeta?.buyerCompany || rfqMeta?.buyer_company || "—").trim();
  const displayId = String(rfqMeta?.displayId || rfqId).trim();

  const user = StorageService.getUser() || {};
  const sellerCompany = String(user.company_name || user.name || "").trim() || "—";
  const sellerPhone = String(user.phone || user.mobile || user.contact_number || "").trim();
  const sellerEmail = String(user.email || "").trim();

  const nBatchVal = Number(String(batchSize || "").replace(/,/g, "").trim());
  const nSamplesVal = Number(String(samplesInspected || "").replace(/,/g, "").trim());
  const errFacility = submitAttempted && !String(facilityName || "").trim();
  const errInspector = submitAttempted && !String(inspectorName || "").trim();
  const errDate = submitAttempted && !String(inspectionDate || "").trim();
  const errBatch = submitAttempted && !isValidPositiveNumberStr(batchSize);
  const errSamples =
    submitAttempted &&
    (!isValidPositiveNumberStr(samplesInspected) ||
      (Number.isFinite(nBatchVal) && Number.isFinite(nSamplesVal) && nSamplesVal > nBatchVal));
  const errAql = submitAttempted && !String(aqlApplied || "").trim();
  const errLab = submitAttempted && !String(labTestResults || "").trim();
  const hasFailedInspection = checklist.some((c) => c.status === "fail");
  const errCompUpload =
    submitAttempted &&
    !(
      uploadFiles.comprehensive?.name ||
      (typeof uploadDisplayNames.comprehensive === "string" && uploadDisplayNames.comprehensive.trim())
    );
  const errPhotosUpload =
    submitAttempted &&
    !(uploadFiles.photos?.length || (Array.isArray(uploadDisplayNames.photos) && uploadDisplayNames.photos.filter(Boolean).length));
  const errNcUpload =
    submitAttempted &&
    hasFailedInspection &&
    !(
      uploadFiles.nonConformance?.name ||
      (typeof uploadDisplayNames.nonConformance === "string" && uploadDisplayNames.nonConformance.trim())
    );

  if (loading) {
    return (
      <div className="cm-insp-page cm-insp-page--loading">
        <p className="text-muted">Loading inspection…</p>
      </div>
    );
  }

  return (
    <div className="cm-insp-page">
      <div className="cm-insp-page__top">
        <button type="button" className="cm-insp-back" onClick={() => navigate("/seller/cm-rfqs")}>
          <FaArrowLeft aria-hidden />
          Back to CM RFQs
        </button>
        <nav className="cm-insp-breadcrumb" aria-label="Breadcrumb">
          <FaHome aria-hidden className="cm-insp-breadcrumb__icon" />
          <Link to="/dashboard/seller">Home</Link>
          <span className="cm-insp-breadcrumb__sep">/</span>
          <Link to="/seller/cm-rfqs">Quality Control</Link>
          <span className="cm-insp-breadcrumb__sep">/</span>
          <span>Internal QC Inspection</span>
        </nav>
      </div>

      <header className="cm-insp-header">
        <div>
          <p className="cm-insp-header__crumbs" aria-hidden>
            Home &gt; Quality Control &gt; Internal QC Inspection
          </p>
          <h1 className="cm-insp-header__title">Internal QC Inspection — {displayId}</h1>
          <p className="cm-insp-header__sub">In-house quality control inspection at your facility</p>
        </div>
      </header>

      <section className="cm-insp-meta-bar" aria-label="Order context">
        <div className="cm-insp-meta-bar__items">
          <span className="cm-insp-meta-bar__item">
            <strong>Order:</strong> ORD-CM-{rfqId}
          </span>
          <span className="cm-insp-meta-bar__item">
            <strong>Manufacturer:</strong> {sellerCompany}
          </span>
          <span className="cm-insp-meta-bar__item">
            <strong>Product:</strong> {productCode}
          </span>
          <span className="cm-insp-meta-bar__item">
            <strong>Buyer:</strong> {buyerCo}
          </span>
        </div>
        <div className="cm-insp-meta-bar__contact">
          {sellerPhone ? <div>{sellerPhone}</div> : null}
          {sellerEmail ? (
            <div>
              <a href={`mailto:${sellerEmail}`}>{sellerEmail}</a>
            </div>
          ) : null}
        </div>
      </section>

      {readOnly ? (
        <div className="cm-insp-banner cm-insp-banner--done" role="status">
          Report submitted on{" "}
          {qcPayload?.cmInternalInspectionReportSubmittedAt
            ? String(qcPayload.cmInternalInspectionReportSubmittedAt).slice(0, 10)
            : "—"}
          . This inspection is read-only.
        </div>
      ) : null}

      <section className="cm-insp-stats" aria-label="Inspection summary">
        <div className="cm-insp-stat">
          <span className="cm-insp-stat__label">Total checks</span>
          <span className="cm-insp-stat__value">{stats.total}</span>
        </div>
        <div className="cm-insp-stat cm-insp-stat--pass">
          <span className="cm-insp-stat__label">Passed</span>
          <span className="cm-insp-stat__value">{stats.passed}</span>
        </div>
        <div className="cm-insp-stat cm-insp-stat--fail">
          <span className="cm-insp-stat__label">Failed</span>
          <span className="cm-insp-stat__value">{stats.failed}</span>
        </div>
        <div className="cm-insp-stat cm-insp-stat--pending">
          <span className="cm-insp-stat__label">Pending</span>
          <span className="cm-insp-stat__value">{stats.pending}</span>
        </div>
        <div className="cm-insp-stat cm-insp-stat--wide">
          <span className="cm-insp-stat__label">Completion</span>
          <div className="cm-insp-progress">
            <div className="cm-insp-progress__bar" style={{ width: `${stats.pct}%` }} />
          </div>
          <span className="cm-insp-stat__pct">{stats.pct}%</span>
        </div>
      </section>

      <section className="cm-insp-agency">
        <div className="cm-insp-agency__grid">
          <label className="cm-insp-field">
            <span className="cm-insp-field__label">
              Facility / team name <span className="cm-insp-req-star" aria-hidden>*</span>
            </span>
            <input
              type="text"
              className={`cm-insp-input${errFacility ? " cm-insp-input--error" : ""}`}
              value={facilityName}
              onChange={(e) => setFacilityName(e.target.value)}
              disabled={readOnly}
              placeholder="e.g. In-house QC"
              aria-invalid={errFacility}
              aria-required
            />
          </label>
          <label className="cm-insp-field">
            <span className="cm-insp-field__label">
              Inspector name <span className="cm-insp-req-star" aria-hidden>*</span>
            </span>
            <input
              type="text"
              className={`cm-insp-input${errInspector ? " cm-insp-input--error" : ""}`}
              value={inspectorName}
              onChange={(e) => setInspectorName(e.target.value)}
              disabled={readOnly}
              placeholder="Inspector name"
              aria-invalid={errInspector}
              aria-required
            />
          </label>
          <label className="cm-insp-field">
            <span className="cm-insp-field__label">
              Inspection date <span className="cm-insp-req-star" aria-hidden>*</span>
            </span>
            <input
              type="date"
              className={`cm-insp-input${errDate ? " cm-insp-input--error" : ""}`}
              value={inspectionDate}
              onChange={(e) => setInspectionDate(e.target.value)}
              disabled={readOnly}
              aria-invalid={errDate}
              aria-required
            />
          </label>
        </div>
      </section>

      <div className="cm-insp-toolbar">
        <div className="cm-insp-tabs" role="tablist">
          {[
            { id: "results", label: "Inspection Results" },
            { id: "compliance", label: "Compliance & Testing" },
            { id: "certificates", label: "Certificates" },
            { id: "uploads", label: "Upload Reports" },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={activeTab === t.id}
              className={`cm-insp-tab ${activeTab === t.id ? "cm-insp-tab--active" : ""}`}
              onClick={() => setActiveTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="cm-insp-toolbar__actions">
          <button type="button" className="cm-insp-btn cm-insp-btn--outline" onClick={handleExport}>
            <FaDownload aria-hidden /> Export
          </button>
          <button type="button" className="cm-insp-btn cm-insp-btn--outline" onClick={handleSaveDraft} disabled={readOnly || saving}>
            <FaSave aria-hidden /> {saving ? "Saving…" : "Save draft"}
          </button>
          <button
            type="button"
            className="cm-insp-btn cm-insp-btn--primary"
            onClick={handleSubmitReport}
            disabled={readOnly || saving}
          >
            <FaUpload aria-hidden /> {saving ? "Submitting…" : "Submit inspection report"}
          </button>
        </div>
      </div>

      <div className={`cm-insp-body${activeTab === "compliance" ? " cm-insp-body--compliance" : ""}`}>
        {activeTab === "results" && (
          <div className="cm-insp-panel">
            {!readOnly ? (
              <p className="cm-insp-panel__req-note">
                <span className="cm-insp-req-star" aria-hidden>*</span> Checklist lines come from the agreed CM QC requirements (
                <strong>Inspection checklist</strong> in the QC package). Each check must be marked <strong>Pass</strong> or{" "}
                <strong>Fail</strong> before submit.
              </p>
            ) : null}
            {checklist.map((item) => (
              <article
                key={item.id}
                className={`cm-insp-card${submitAttempted && item.status === "pending" ? " cm-insp-card--invalid" : ""}`}
              >
                <div className="cm-insp-card__head">
                  <span className="cm-insp-card__cat">{item.category}</span>
                  <span
                    className={`cm-insp-badge cm-insp-badge--${item.status === "pass" ? "pass" : item.status === "fail" ? "fail" : "pending"}`}
                  >
                    {item.status === "pass" ? "Passed" : item.status === "fail" ? "Failed" : "Pending"}
                  </span>
                </div>
                <h3 className="cm-insp-card__title">{item.title}</h3>
                <p className="cm-insp-card__criteria">
                  <strong>Acceptance criteria:</strong> {item.criteria}
                </p>
                <div className="cm-insp-card__actions">
                  <button
                    type="button"
                    className={`cm-insp-result-btn cm-insp-result-btn--pass ${item.status === "pass" ? "is-on" : ""}`}
                    disabled={readOnly}
                    onClick={() => setCheckStatus(item.id, "pass")}
                  >
                    <FaCheck aria-hidden /> Pass
                  </button>
                  <button
                    type="button"
                    className={`cm-insp-result-btn cm-insp-result-btn--fail ${item.status === "fail" ? "is-on" : ""}`}
                    disabled={readOnly}
                    onClick={() => setCheckStatus(item.id, "fail")}
                  >
                    <FaTimes aria-hidden /> Fail
                  </button>
                  <button
                    type="button"
                    className={`cm-insp-result-btn cm-insp-result-btn--pending ${item.status === "pending" ? "is-on" : ""}`}
                    disabled={readOnly}
                    onClick={() => setCheckStatus(item.id, "pending")}
                  >
                    <FaInfoCircle aria-hidden /> Pending
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}

        {activeTab === "compliance" && (
          <div className="cm-insp-compliance-stack">
            <section className="cm-insp-c-card">
              <h3 className="cm-insp-c-card__title">Sample information</h3>
              <div className="cm-insp-c-sample-row">
                <label className="cm-insp-c-field">
                  <span className="cm-insp-c-field__label">
                    Batch size <span className="cm-insp-req-star" aria-hidden>*</span>
                  </span>
                  <input
                    type="text"
                    className={`cm-insp-c-input${errBatch ? " cm-insp-c-input--error" : ""}`}
                    inputMode="numeric"
                    value={batchSize}
                    onChange={(e) => setBatchSize(e.target.value)}
                    disabled={readOnly}
                    placeholder="e.g. 5000"
                    aria-invalid={errBatch}
                    aria-required
                  />
                </label>
                <label className="cm-insp-c-field">
                  <span className="cm-insp-c-field__label">
                    Samples inspected <span className="cm-insp-req-star" aria-hidden>*</span>
                  </span>
                  <input
                    type="text"
                    className={`cm-insp-c-input${errSamples ? " cm-insp-c-input--error" : ""}`}
                    inputMode="numeric"
                    value={samplesInspected}
                    onChange={(e) => setSamplesInspected(e.target.value)}
                    disabled={readOnly}
                    placeholder={
                      qcNormsFromPayload.samplePct
                        ? `From norms: ${qcNormsFromPayload.samplePct}% of batch`
                        : "e.g. 500"
                    }
                    aria-invalid={errSamples}
                    aria-required
                  />
                </label>
                <label className="cm-insp-c-field">
                  <span className="cm-insp-c-field__label">
                    AQL applied <span className="cm-insp-req-star" aria-hidden>*</span>
                  </span>
                  <select
                    className={`cm-insp-c-input cm-insp-c-input--select${errAql ? " cm-insp-c-input--error" : ""}`}
                    value={aqlApplied}
                    onChange={(e) => setAqlApplied(e.target.value)}
                    disabled={readOnly}
                    aria-invalid={errAql}
                    aria-required
                  >
                    {aqlSelectOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              {qcNormsFromPayload.samplePct || qcNormsFromPayload.minPassPct ? (
                <p className="cm-insp-c-norms-note" role="note">
                  <strong>QC norms:</strong>{" "}
                  {qcNormsFromPayload.samplePct ? (
                    <>
                      Sample size <strong>{qcNormsFromPayload.samplePct}%</strong> of lot
                    </>
                  ) : null}
                  {qcNormsFromPayload.minPassPct ? (
                    <>
                      {qcNormsFromPayload.samplePct ? " · " : null}
                      Minimum pass rate <strong>{qcNormsFromPayload.minPassPct}%</strong>
                    </>
                  ) : null}
                  {qcNormsFromPayload.aql ? (
                    <>
                      {" · "}
                      AQL <strong>{qcNormsFromPayload.aql}</strong>
                    </>
                  ) : null}
                  {qcNormsFromPayload.samplePct && batchSize.trim() ? (
                    <span className="cm-insp-c-norms-note__hint">
                      {" "}
                      — Suggested count from batch size:{" "}
                      <strong>{suggestedSamplesFromBatch(batchSize, qcNormsFromPayload.samplePct) ?? "—"}</strong>
                    </span>
                  ) : null}
                </p>
              ) : null}
            </section>

            <section className="cm-insp-c-card">
              <h3 className="cm-insp-c-card__title">
                Compliance standards tested <span className="cm-insp-req-star" aria-hidden>*</span>
              </h3>
              {!readOnly ? (
                <p className="cm-insp-c-card__hint">Each standard must be set to Pass or Fail before submit.</p>
              ) : null}
              <div className="cm-insp-c-standards-grid">
                {COMPLIANCE_STANDARDS.map((s) => (
                  <div
                    key={s.id}
                    className={`cm-insp-c-standard${
                      submitAttempted && (complianceStatus[s.id] || "pending") === "pending"
                        ? " cm-insp-c-standard--invalid"
                        : ""
                    }`}
                  >
                    <div className="cm-insp-c-standard__text">
                      <div className="cm-insp-c-standard__name">{s.label}</div>
                      <div className="cm-insp-c-standard__sub">{s.sub}</div>
                    </div>
                    <select
                      className="cm-insp-c-input cm-insp-c-input--select cm-insp-c-input--status"
                      value={complianceStatus[s.id] || "pending"}
                      onChange={(e) =>
                        setComplianceStatus((prev) => ({ ...prev, [s.id]: e.target.value }))
                      }
                      disabled={readOnly}
                    >
                      <option value="pending">Pending</option>
                      <option value="pass">Pass</option>
                      <option value="fail">Fail</option>
                    </select>
                  </div>
                ))}
              </div>
            </section>

            <section className="cm-insp-c-card">
              <h3 className="cm-insp-c-card__title">
                Lab test results <span className="cm-insp-req-star" aria-hidden>*</span>
              </h3>
              <p className="cm-insp-c-card__hint">Required. Use &quot;N/A&quot; if no lab testing applied.</p>
              <textarea
                className={`cm-insp-c-lab${errLab ? " cm-insp-c-lab--error" : ""}`}
                rows={8}
                value={labTestResults}
                onChange={(e) => setLabTestResults(e.target.value)}
                disabled={readOnly}
                placeholder="Enter detailed laboratory test results, material analysis, performance testing outcomes, and compliance verification…"
                aria-invalid={errLab}
                aria-required
              />
            </section>
          </div>
        )}

        {activeTab === "certificates" && (
          <div className="cm-insp-panel">
            <div className="cm-insp-cert-head">
              <FaAward className="cm-insp-cert-head__icon" aria-hidden />
              <div>
                <h2 className="cm-insp-cert-head__title">Inspection certificates &amp; compliance documents</h2>
                <p className="cm-insp-cert-head__desc">
                  Upload the documents listed below as agreed under <strong>Internal QC document requirements</strong> in your QC
                  norms. Official certificates should include seal and authorized signatures where applicable.
                </p>
              </div>
            </div>
            {certificateSlots.length === 0 ? (
              <div className="cm-insp-empty-docs" role="status">
                No internal QC documents were defined in the agreed QC requirements. Add them in the QC requirements modal
                (Internal QC Docs tab) or ask the buyer to update the QC package.
              </div>
            ) : (
              <div className="cm-insp-upload-grid">
                {certificateSlots.map((doc, idx) => {
                  const Icon = CERT_ICONS[idx % CERT_ICONS.length];
                  const selected = certFiles[doc.id];
                  const persistedName = certDisplayNames[doc.id];
                  const showName = selected?.name || persistedName;
                  const certMissing =
                    submitAttempted &&
                    doc.required &&
                    !(certFiles[doc.id] || (certDisplayNames[doc.id] && String(certDisplayNames[doc.id]).trim()));
                  return (
                    <label
                      key={doc.id}
                      className={`cm-insp-drop${certMissing ? " cm-insp-drop--invalid" : ""}`}
                    >
                      <input
                        type="file"
                        accept={acceptForDocFormat(doc.fileFormat)}
                        className="cm-insp-drop__input"
                        disabled={readOnly}
                        onChange={(e) => {
                          const f = e.target.files?.[0] || null;
                          setCertFiles((prev) => ({ ...prev, [doc.id]: f }));
                          setCertDisplayNames((prev) => ({
                            ...prev,
                            [doc.id]: f ? f.name : prev[doc.id] || "",
                          }));
                        }}
                      />
                      <Icon className="cm-insp-drop__icon" aria-hidden />
                      <div className="cm-insp-drop__title-row">
                        <span className="cm-insp-drop__title">{doc.name}</span>
                        {doc.required ? (
                          <span className="cm-insp-drop__required">Required</span>
                        ) : (
                          <span className="cm-insp-drop__optional">Optional</span>
                        )}
                      </div>
                      <div className="cm-insp-drop__sub">{formatHintForDoc(doc)}</div>
                      {showName ? <div className="cm-insp-drop__file">{showName}</div> : null}
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === "uploads" && (
          <div className="cm-insp-panel">
            <div className="cm-insp-upload-banner">
              Required: comprehensive report, photo documentation, measurement data where applicable, and non-conformance reports if any issues
              were found.
            </div>
            <div className="cm-insp-upload-grid">
              <label className={`cm-insp-drop${errCompUpload ? " cm-insp-drop--invalid" : ""}`}>
                <input
                  type="file"
                  accept=".pdf,application/pdf"
                  className="cm-insp-drop__input"
                  disabled={readOnly}
                  onChange={(e) => {
                    const f = e.target.files?.[0] || null;
                    setUploadFiles((p) => ({ ...p, comprehensive: f }));
                    setUploadDisplayNames((prev) => ({
                      ...prev,
                      comprehensive: f ? f.name : prev.comprehensive,
                    }));
                  }}
                />
                <FaUpload className="cm-insp-drop__icon" aria-hidden />
                <div className="cm-insp-drop__title">
                  Comprehensive inspection report <span className="cm-insp-req-star" aria-hidden>*</span>
                </div>
                <div className="cm-insp-drop__sub">PDF, max 20MB</div>
                {uploadFiles.comprehensive?.name || uploadDisplayNames.comprehensive ? (
                  <div className="cm-insp-drop__file">
                    {uploadFiles.comprehensive?.name || uploadDisplayNames.comprehensive}
                  </div>
                ) : null}
              </label>
              <label className={`cm-insp-drop${errPhotosUpload ? " cm-insp-drop--invalid" : ""}`}>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  className="cm-insp-drop__input"
                  disabled={readOnly}
                  onChange={(e) => {
                    const list = Array.from(e.target.files || []);
                    setUploadFiles((p) => ({ ...p, photos: list }));
                    setUploadDisplayNames((prev) => ({
                      ...prev,
                      photos: list.length ? list.map((x) => x.name) : prev.photos,
                    }));
                  }}
                />
                <FaCamera className="cm-insp-drop__icon" aria-hidden />
                <div className="cm-insp-drop__title">
                  Photo documentation <span className="cm-insp-req-star" aria-hidden>*</span>
                </div>
                <div className="cm-insp-drop__sub">JPG, PNG — up to 20 files</div>
                {uploadFiles.photos?.length ? (
                  <div className="cm-insp-drop__file">{uploadFiles.photos.length} file(s) selected</div>
                ) : uploadDisplayNames.photos?.length ? (
                  <div className="cm-insp-drop__file">
                    {uploadDisplayNames.photos.length} file(s) in draft: {uploadDisplayNames.photos.join(", ")}
                  </div>
                ) : null}
              </label>
              <label className="cm-insp-drop">
                <input
                  type="file"
                  accept=".pdf,.csv,.xlsx,application/pdf,text/csv"
                  className="cm-insp-drop__input"
                  disabled={readOnly}
                  onChange={(e) => {
                    const f = e.target.files?.[0] || null;
                    setUploadFiles((p) => ({ ...p, measurement: f }));
                    setUploadDisplayNames((prev) => ({
                      ...prev,
                      measurement: f ? f.name : prev.measurement,
                    }));
                  }}
                />
                <FaFileAlt className="cm-insp-drop__icon" aria-hidden />
                <div className="cm-insp-drop__title">Measurement & test data</div>
                <div className="cm-insp-drop__sub">Excel, CSV, PDF (max 10MB)</div>
                {uploadFiles.measurement?.name || uploadDisplayNames.measurement ? (
                  <div className="cm-insp-drop__file">
                    {uploadFiles.measurement?.name || uploadDisplayNames.measurement}
                  </div>
                ) : null}
              </label>
              <label className={`cm-insp-drop${errNcUpload ? " cm-insp-drop--invalid" : ""}`}>
                <input
                  type="file"
                  accept=".pdf,application/pdf"
                  className="cm-insp-drop__input"
                  disabled={readOnly}
                  onChange={(e) => {
                    const f = e.target.files?.[0] || null;
                    setUploadFiles((p) => ({ ...p, nonConformance: f }));
                    setUploadDisplayNames((prev) => ({
                      ...prev,
                      nonConformance: f ? f.name : prev.nonConformance,
                    }));
                  }}
                />
                <FaExclamationCircle className="cm-insp-drop__icon" aria-hidden />
                <div className="cm-insp-drop__title">
                  Non-conformance report
                  {hasFailedInspection ? <span className="cm-insp-req-star" aria-hidden>*</span> : null}
                </div>
                <div className="cm-insp-drop__sub">
                  {hasFailedInspection
                    ? "Required when any inspection check is Fail — PDF max 10MB"
                    : "If defects were identified — PDF max 10MB"}
                </div>
                {uploadFiles.nonConformance?.name || uploadDisplayNames.nonConformance ? (
                  <div className="cm-insp-drop__file">
                    {uploadFiles.nonConformance?.name || uploadDisplayNames.nonConformance}
                  </div>
                ) : null}
              </label>
            </div>
          </div>
        )}
      </div>

      <footer className="cm-insp-footer">
        <div className="cm-insp-footer__summary">
          Inspection status: {stats.passed} Passed • {stats.failed} Failed • {stats.pending} Pending
        </div>
        <div className="cm-insp-footer__actions">
          <button type="button" className="cm-insp-btn cm-insp-btn--outline" onClick={() => navigate("/seller/cm-rfqs")}>
            Cancel
          </button>
          <button type="button" className="cm-insp-btn cm-insp-btn--outline" onClick={handleSaveDraft} disabled={readOnly || saving}>
            <FaSave aria-hidden /> {saving ? "Saving…" : "Save draft"}
          </button>
          <button
            type="button"
            className="cm-insp-btn cm-insp-btn--primary cm-insp-btn--lg"
            onClick={handleSubmitReport}
            disabled={readOnly || saving}
          >
            <FaUpload aria-hidden /> {saving ? "Submitting…" : "Submit inspection report"}
          </button>
        </div>
      </footer>
    </div>
  );
}
